import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GsdReadableBlock } from "@/coquille-bridge";
import { useAuth } from "@/context/AuthContext.jsx";
import { useKarateUi } from "@/hooks/useKarateUi.js";
import { getDefaultRouteForRole } from "@/data/dojoOsOrgModel.js";
import { PageSection } from "@/components/AppLayout.jsx";
import { GsdPasswordField } from "@/components/auth/GsdPasswordField.jsx";

const PASSWORD_MIN_LENGTH = 6;

export function ChangePasswordPage() {
  const { changeBackendPassword, mustChangePassword, canChangePassword, activeRoleUi, isAuthenticated } =
    useAuth();
  const navigate = useNavigate();
  const { ui, isEn } = useKarateUi();
  const sec = ui.authSecurity;
  const authLabels = ui.accountMenu;
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!isAuthenticated || !canChangePassword) {
    return (
      <PageSection title={sec.changePasswordTitle} testId="page-change-password">
        <GsdReadableBlock className="auth-security-notice" testId="change-password-guest-notice">
          <p>{isEn ? "Sign in to change your password." : "Connectez-vous pour changer votre mot de passe."}</p>
        </GsdReadableBlock>
      </PageSection>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      setError(sec.passwordMinLengthHint);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(sec.confirmMismatch);
      return;
    }

    setLoading(true);
    const result = await changeBackendPassword({ currentPassword, newPassword, confirmPassword });
    setLoading(false);
    if (!result.ok) {
      setError(result.message || sec.updateFailed);
      return;
    }
    setSuccess(result.message || sec.success);
    const role = activeRoleUi || "student";
    navigate(getDefaultRouteForRole(role));
  };

  const pwdProps = {
    showLabel: authLabels.showPassword,
    hideLabel: authLabels.hidePassword,
    className: "change-password-form__field",
    inputClassName: "cq-login-card__input",
  };

  return (
    <PageSection title={sec.changePasswordTitle} testId="page-change-password">
      {mustChangePassword ? (
        <GsdReadableBlock className="auth-security-notice" testId="change-password-required-notice">
          <p>{sec.changePasswordRequired}</p>
        </GsdReadableBlock>
      ) : null}

      <p className="change-password-policy" data-testid="change-password-policy-hint">
        {sec.passwordMinLengthHint}
      </p>

      <form className="change-password-form" onSubmit={handleSubmit} data-testid="change-password-form" noValidate>
        <GsdPasswordField
          label={sec.currentPassword}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
          testIdPrefix="change-password-current"
          inputTestId="change-password-current"
          toggleTestId="change-password-current-toggle"
          {...pwdProps}
        />
        <GsdPasswordField
          label={sec.newPassword}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          testIdPrefix="change-password-new"
          inputTestId="change-password-new"
          toggleTestId="change-password-new-toggle"
          {...pwdProps}
        />
        <GsdPasswordField
          label={sec.confirmPassword}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          testIdPrefix="change-password-confirm"
          inputTestId="change-password-confirm"
          toggleTestId="change-password-confirm-toggle"
          {...pwdProps}
        />
        {error ? (
          <p className="form-error" role="alert" data-testid="change-password-error">
            {error}
          </p>
        ) : null}
        {success ? (
          <p className="form-success" role="status" data-testid="change-password-success">
            {success}
          </p>
        ) : null}
        <button
          type="submit"
          className="btn-primary change-password-form__submit"
          disabled={loading}
          data-testid="change-password-submit"
        >
          {loading ? sec.submitting : sec.submit}
        </button>
      </form>
    </PageSection>
  );
}
